/**
 * Couche catalogue — regroupement, index de personnes, sélections éditoriales.
 *
 * Tout est calculé côté client à partir de la liste déjà chargée par
 * `apiService.getMovies()` : aucune requête supplémentaire, aucun changement
 * de backend. Les fonctions sont pures pour rester enveloppables dans `useMemo`.
 */

/* ═══════════════════════════════ Noms ═══════════════════════════════ */

/** Clé de comparaison insensible à la casse, aux accents et à la ponctuation. */
export function normalizeName(value) {
  return (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // marques diacritiques isolées par NFD
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Initiales pour les avatars sans photo. */
export function initials(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

/* ═══════════════════════════ Regroupement ═══════════════════════════ */

/**
 * Réduit la liste plate (un enregistrement par épisode) en titres :
 * les films restent tels quels, les épisodes sont fusionnés en une série.
 *
 * La série hérite des métadonnées du premier épisode rencontré, et expose :
 *   - `id`            : `series_<tmdbId>` (identifiant stable et unique)
 *   - `isGroupedSeries` / `episodes` / `seasonCount` / `episodeCount`
 *   - `addedAt`       : date d'ajout de l'épisode le plus récent, pour que
 *                       les nouveaux épisodes fassent remonter la série.
 */
export function groupSeries(movies) {
  if (!Array.isArray(movies)) return [];

  const films = [];
  const series = new Map();

  for (const movie of movies) {
    if (movie.type !== 'serie') {
      films.push(movie);
      continue;
    }

    let entry = series.get(movie.tmdbId);
    if (!entry) {
      entry = {
        ...movie,
        id: `series_${movie.tmdbId}`,
        isGroupedSeries: true,
        episodes: [],
      };
      series.set(movie.tmdbId, entry);
    }
    entry.episodes.push(movie);

    // La série est « fraîche » dès qu'un seul de ses épisodes l'est.
    if (movie.addedAt && (!entry.addedAt || movie.addedAt > entry.addedAt)) {
      entry.addedAt = movie.addedAt;
    }
  }

  for (const entry of series.values()) {
    entry.episodes.sort(
      (a, b) => (a.season || 0) - (b.season || 0) || (a.episode || 0) - (b.episode || 0)
    );
    entry.episodeCount = entry.episodes.length;
    entry.seasonCount =
      entry.numberOfSeasons || new Set(entry.episodes.map((e) => e.season)).size;
  }

  return [...films, ...series.values()];
}

/** Tous les épisodes d'une série, dans l'ordre. */
export function episodesOf(movies, tmdbId) {
  return movies
    .filter((m) => m.type === 'serie' && String(m.tmdbId) === String(tmdbId))
    .sort((a, b) => (a.season || 0) - (b.season || 0) || (a.episode || 0) - (b.episode || 0));
}

/* ══════════════════════ Index des personnes ═════════════════════════ */

/**
 * Construit l'index acteurs / réalisateurs à partir des **titres groupés**.
 *
 * Le regroupement préalable est indispensable : dans la base, chaque épisode
 * répète la distribution de la série. Sans lui, un acteur de série
 * apparaîtrait des centaines de fois (219 occurrences pour X-Files).
 *
 * @param {Array} titles sortie de `groupSeries()`
 * @returns {{actors: Array, directors: Array, actorsByKey: Map, directorsByKey: Map}}
 */
export function buildPeopleIndex(titles) {
  const actorsByKey = new Map();
  const directorsByKey = new Map();

  const push = (registry, rawName, title, billing) => {
    const name = (rawName || '').trim();
    if (!name) return;
    const key = normalizeName(name);
    if (!key) return;

    let person = registry.get(key);
    if (!person) {
      person = { key, name, titles: [], films: 0, series: 0, topBilling: 99 };
      registry.set(key, person);
    }
    person.titles.push(title);
    if (title.type === 'serie') person.series += 1;
    else person.films += 1;
    if (billing < person.topBilling) person.topBilling = billing;
  };

  for (const title of titles) {
    const cast = Array.isArray(title.cast) ? title.cast : [];
    cast.forEach((name, index) => push(actorsByKey, name, title, index));
    if (title.director) push(directorsByKey, title.director, title, 0);
  }

  const finish = (registry) => {
    const list = [];
    for (const person of registry.values()) {
      person.count = person.titles.length;
      // Une note moyenne pondérée sert à mettre en avant les têtes d'affiche
      const rated = person.titles.filter((t) => t.rating > 0);
      person.avgRating = rated.length
        ? rated.reduce((sum, t) => sum + Number(t.rating), 0) / rated.length
        : 0;
      // Poids éditorial : présence dans le catalogue, qualité, place au générique
      person.weight =
        person.count * 10 + person.avgRating * 3 - Math.min(person.topBilling, 10);
      person.titles.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      list.push(person);
    }
    return list.sort((a, b) => b.weight - a.weight || a.name.localeCompare(b.name));
  };

  return {
    actors: finish(actorsByKey),
    directors: finish(directorsByKey),
    actorsByKey,
    directorsByKey,
  };
}

/** Retrouve une personne dans l'index à partir d'un nom brut. */
export function findPerson(index, name) {
  if (!index) return null;
  const key = normalizeName(name);
  return index.actorsByKey.get(key) || index.directorsByKey.get(key) || null;
}

/** Recherche floue par préfixe de mot, triée par pertinence éditoriale. */
export function searchPeople(index, query, limit = 8) {
  const key = normalizeName(query);
  if (!index || key.length < 2) return [];

  const seen = new Set();
  const exact = [];
  const starts = [];
  const contains = [];

  for (const person of [...index.actors, ...index.directors]) {
    if (seen.has(person.key)) continue;
    seen.add(person.key);

    if (person.key === key) exact.push(person);
    // Un début de mot compte autant qu'un début de chaîne : « pitt »
    // doit trouver « Brad Pitt ».
    else if (person.key.startsWith(key) || person.key.includes(` ${key}`)) starts.push(person);
    else if (person.key.includes(key)) contains.push(person);

    if (exact.length + starts.length >= limit * 3) break;
  }

  return [...exact, ...starts, ...contains].slice(0, limit);
}

/* ══════════════════════ Sélections éditoriales ══════════════════════ */

const RECENT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const byNewest = (a, b) => new Date(b.addedAt || 0) - new Date(a.addedAt || 0);
const byRating = (a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0);

/** Titres ajoutés dans les 30 derniers jours (à défaut, les plus récents). */
export function newArrivals(titles, limit = 20) {
  const cutoff = Date.now() - RECENT_WINDOW_MS;
  const fresh = titles.filter((t) => new Date(t.addedAt || 0).getTime() > cutoff);
  return (fresh.length >= 6 ? fresh : [...titles]).sort(byNewest).slice(0, limit);
}

/**
 * Le « Top 10 » : les mieux notés, avec un seuil de votes pour écarter
 * les titres confidentiels à 10/10 sur trois votes.
 */
export function topRated(titles, limit = 10, minVotes = 400) {
  const solid = titles.filter((t) => (t.voteCount || 0) >= minVotes && t.rating > 0);
  return (solid.length >= limit ? solid : titles.filter((t) => t.rating > 0))
    .sort(byRating)
    .slice(0, limit);
}

/** Titres d'un genre donné, les mieux notés d'abord. */
export function byGenre(titles, genre, limit = 20) {
  return titles
    .filter((t) => Array.isArray(t.genres) && t.genres.includes(genre))
    .sort(byRating)
    .slice(0, limit);
}

/** Genres du catalogue, ordonnés par volume. */
export function genreRanking(titles) {
  const tally = new Map();
  for (const title of titles) {
    for (const genre of title.genres || []) {
      tally.set(genre, (tally.get(genre) || 0) + 1);
    }
  }
  return [...tally.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * « Parce que vous avez regardé … » : à partir d'un titre de référence,
 * on remonte les titres qui partagent le plus de genres, puis les mieux notés.
 */
export function similarTo(titles, reference, limit = 20) {
  if (!reference) return [];
  const refGenres = new Set(reference.genres || []);
  const refKey = String(reference.id);
  const refTmdb = String(reference.tmdbId);

  return titles
    .filter((t) => String(t.id) !== refKey && String(t.tmdbId) !== refTmdb)
    .map((t) => {
      const shared = (t.genres || []).filter((g) => refGenres.has(g)).length;
      const sameType = t.type === reference.type ? 1 : 0;
      return { title: t, score: shared * 4 + sameType + (Number(t.rating) || 0) / 4 };
    })
    .filter((entry) => entry.score > 4)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.title);
}

/** Une décennie de sortie, pour les rails « années 90 » et consorts. */
export function byDecade(titles, decade, limit = 20) {
  return titles
    .filter((t) => {
      const year = Number(t.year);
      return year >= decade && year < decade + 10;
    })
    .sort(byRating)
    .slice(0, limit);
}

/* ═══════════════════════════ Recherche ══════════════════════════════ */

/**
 * Recherche de titres : titre, titre original, réalisateur et distribution.
 * C'est ce dernier point qui rend la recherche par acteur possible depuis
 * la barre principale.
 */
export function searchTitles(titles, query, limit = 40) {
  const key = normalizeName(query);
  if (key.length < 2) return [];

  const scored = [];
  for (const title of titles) {
    const name = normalizeName(title.title);
    const original = normalizeName(title.originalTitle);

    let score = 0;
    if (name === key) score = 100;
    else if (name.startsWith(key)) score = 80;
    else if (name.includes(key)) score = 60;
    else if (original.startsWith(key)) score = 55;
    else if (original.includes(key)) score = 40;
    else if (normalizeName(title.director).includes(key)) score = 26;
    else if ((title.cast || []).some((actor) => normalizeName(actor).includes(key))) score = 22;

    if (score) scored.push({ title, score: score + (Number(title.rating) || 0) / 10 });
  }

  return scored.sort((a, b) => b.score - a.score).slice(0, limit).map((s) => s.title);
}

/* ═══════════════════════════ Divers ═════════════════════════════════ */

/** Durée « 1h 43min » ou « 54min » → minutes. */
export function durationToMinutes(duration) {
  if (!duration) return 0;
  const hours = /(\d+)\s*h/.exec(duration);
  const minutes = /(\d+)\s*min/.exec(duration);
  return (hours ? Number(hours[1]) * 60 : 0) + (minutes ? Number(minutes[1]) : 0);
}

/** Libellé court sous une affiche : « 2022 · 1h43 » / « 3 saisons ». */
export function subtitleFor(title) {
  if (title.isGroupedSeries || title.type === 'serie') {
    const seasons = title.seasonCount || title.numberOfSeasons || 1;
    return `${seasons} saison${seasons > 1 ? 's' : ''}`;
  }
  const total = durationToMinutes(title.duration);
  if (!total) return title.year ? String(title.year) : '';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? `${h}h${String(m).padStart(2, '0')}` : `${m}min`;
}
